// SettlementReport.js — client-side PDF generation via jsPDF + jspdf-autotable
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';

/**
 * Compute minimal who-owes-whom from participant balances when
 * the bill doesn't already have a whoOwesWhom array.
 */
function deriveSettlements(participants) {
  const credits = participants
    .map((p) => ({ name: p.name, amt: (p.paidAmount || 0) - (p.share || 0) }))
    .filter((p) => p.amt > 0.005);
  const debts = participants
    .map((p) => ({ name: p.name, amt: (p.share || 0) - (p.paidAmount || 0) }))
    .filter((p) => p.amt > 0.005);

  const result = [];
  let ci = 0; let di = 0;
  while (ci < credits.length && di < debts.length) {
    const pay = Math.min(credits[ci].amt, debts[di].amt);
    if (pay > 0.005) result.push({ fromName: debts[di].name, toName: credits[ci].name, amount: pay });
    credits[ci].amt -= pay;
    debts[di].amt -= pay;
    if (credits[ci].amt < 0.005) ci++;
    if (debts[di].amt < 0.005) di++;
  }
  return result;
}

/**
 * Generate and download a settlement report PDF for the given bill object.
 */
export function generateSettlementPDF(bill) {
  const currency = bill.currency || 'EUR';
  const fmt = (n) => `${currency} ${parseFloat(n || 0).toFixed(2)}`;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 14;
  const CONTENT_W = PAGE_W - MARGIN * 2;
  const PRIMARY = [30, 64, 175];       // #1e40af
  const SUCCESS = [16, 185, 129];      // #10b981
  const DANGER  = [239, 68, 68];       // #ef4444
  const MUTED   = [156, 163, 175];     // #9ca3af
  const DARK    = [17, 24, 39];        // #111827

  let y = MARGIN;

  // ── Header banner ──────────────────────────────────────────
  doc.setFillColor(...PRIMARY);
  doc.roundedRect(MARGIN, y, CONTENT_W, 36, 4, 4, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('SOLDIKEEPER · SETTLEMENT REPORT', MARGIN + 6, y + 7);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(bill.title, MARGIN + 6, y + 18);

  const dateStr = bill.date
    ? new Date(bill.date).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    [dateStr, bill.category, bill.splitMethod ? `${bill.splitMethod} split` : ''].filter(Boolean).join('  ·  '),
    MARGIN + 6,
    y + 26,
  );

  // Total amount badge
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(fmt(bill.totalAmount), PAGE_W - MARGIN - 6, y + 18, { align: 'right' });
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('TOTAL', PAGE_W - MARGIN - 6, y + 25, { align: 'right' });

  y += 42;

  // ── Stats row ─────────────────────────────────────────────
  const totalPaid = bill.participants.reduce((s, p) => s + (p.paidAmount || 0), 0);
  const outstanding = bill.participants.reduce(
    (s, p) => s + Math.max(0, (p.share || 0) - (p.paidAmount || 0)), 0,
  );
  const stats = [
    { label: 'PARTICIPANTS', value: String(bill.participants.length), color: PRIMARY },
    { label: 'COLLECTED',    value: fmt(totalPaid),                   color: SUCCESS },
    { label: 'OUTSTANDING',  value: fmt(outstanding),                 color: outstanding > 0.005 ? DANGER : SUCCESS },
  ];
  const boxW = (CONTENT_W - 8) / 3;
  stats.forEach((s, i) => {
    const bx = MARGIN + i * (boxW + 4);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(bx, y, boxW, 18, 3, 3, 'F');
    doc.setTextColor(...s.color);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(s.value, bx + boxW / 2, y + 10, { align: 'center' });
    doc.setTextColor(...MUTED);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(s.label, bx + boxW / 2, y + 15.5, { align: 'center' });
  });
  y += 24;

  doc.setTextColor(...DARK);

  // ── Section: Settlement Instructions ──────────────────────
  const settlements =
    bill.whoOwesWhom && bill.whoOwesWhom.length > 0
      ? bill.whoOwesWhom
      : deriveSettlements(bill.participants);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('💸  SETTLEMENT INSTRUCTIONS', MARGIN, y);
  y += 3;

  if (settlements.length === 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...SUCCESS);
    doc.text('✓  All settled up!', MARGIN, y + 6);
    doc.setTextColor(...DARK);
    y += 12;
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [['From', 'Owes →', 'To', 'Amount', 'Status']],
      body: settlements.map((s) => [
        s.fromName,
        '→',
        s.toName,
        fmt(s.amount),
        (s.status || 'pending').toUpperCase(),
      ]),
      headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        1: { textColor: MUTED, fontSize: 8 },
        3: { fontStyle: 'bold', textColor: DANGER },
        4: { fontSize: 7.5, textColor: MUTED },
      },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      styles: { cellPadding: 3.5 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Section: Participant Balances ──────────────────────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text('👥  PARTICIPANT BALANCES', MARGIN, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [['Name', 'Share Owed', 'Paid', 'Balance', 'Status']],
    body: bill.participants.map((p) => {
      const paid   = parseFloat(p.paidAmount || 0);
      const share  = parseFloat(p.share || 0);
      const bal    = paid - share;
      const status = p.hasPaid ? 'SETTLED' : paid > 0 ? 'PARTIAL' : 'PENDING';
      return [
        p.name + (p.isCreator ? ' (host)' : ''),
        fmt(share),
        fmt(paid),
        (bal >= 0 ? '+' : '') + fmt(bal),
        status,
      ];
    }),
    headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      3: {
        fontStyle: 'bold',
        // colour applied per-cell via willDrawCell below
      },
      4: { fontSize: 7.5 },
    },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    styles: { cellPadding: 3.5 },
    willDrawCell(data) {
      if (data.section === 'body') {
        if (data.column.index === 3) {
          const val = parseFloat((data.cell.raw || '').replace(/[^\d.-]/g, ''));
          doc.setTextColor(...(val >= 0 ? SUCCESS : DANGER));
        } else if (data.column.index === 4) {
          const s = String(data.cell.raw);
          doc.setTextColor(...(s === 'SETTLED' ? SUCCESS : s === 'PARTIAL' ? [245, 158, 11] : MUTED));
        } else {
          doc.setTextColor(...DARK);
        }
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── Section: Expense Breakdown ─────────────────────────────
  if (bill.expenses && bill.expenses.length > 0) {
    // New page if not enough room
    if (y > 220) { doc.addPage(); y = MARGIN; }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK);
    doc.text(`🧾  EXPENSE BREAKDOWN (${bill.expenses.length} items)`, MARGIN, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Date', 'Description', 'Paid By', 'Amount']],
      body: bill.expenses.map((e) => {
        const paidByName =
          e.paidByName ||
          (bill.participants[e.paidBy]?.name || `Participant ${e.paidBy}`);
        return [
          e.date ? new Date(e.date).toLocaleDateString('en-GB') : '—',
          e.description,
          paidByName,
          fmt(e.amount),
        ];
      }),
      headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
      bodyStyles: { fontSize: 8.5 },
      columnStyles: { 3: { fontStyle: 'bold', halign: 'right' } },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      styles: { cellPadding: 3 },
      foot: [['', '', 'Total', fmt(bill.totalAmount)]],
      footStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Footer ────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footY = doc.internal.pageSize.getHeight() - 8;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(
      `Generated by SoldiKeeper on ${new Date().toLocaleDateString('en-GB')}  ·  soldikeeper.com`,
      PAGE_W / 2,
      footY,
      { align: 'center' },
    );
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W - MARGIN, footY, { align: 'right' });
  }

  const filename = `settlement-report-${bill.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`;
  doc.save(filename);
}

/**
 * Returns the public web report URL for a bill (requires shareCode).
 * Generates the shareCode via API if needed.
 */
export function getReportWebUrl(bill) {
  if (!bill.shareCode) return null;
  return `${API_BASE.replace('/api', '')}/api/split-bills/report/${bill.shareCode}`;
}

/**
 * Copy the report web URL to clipboard and return success boolean.
 */
export async function copyReportLink(bill) {
  const url = getReportWebUrl(bill);
  if (!url) return false;
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}
